import type { Breadcrumb } from "@m10s/mobility-mapping-utils";
import { getDiscoveryPath } from "@m10s/mobility-mapping-utils";
import type { MobilityAd } from "@mobility-item-page/types";
import { extractCarTaxonomy } from "@mobility-item-page/utilities";
import type { Logger } from "pino";
import { useAdViewAd, useAdViewMeta } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";

const SearchLinks = () => {
  const ad = useAdViewAd();

  const searchLinks = useSearchLinks(ad);
  if (searchLinks === null) return null;

  return searchLinks?.length ? (
    <div className="flex py-8 mt-16 md:mt-24">
      {searchLinks.map(({ url, text }) => (
        <div className="mr-16" key={url}>
          <a href={url}>{text}</a>
        </div>
      ))}
    </div>
  ) : null;
};
export default SearchLinks;

const CreateSearchLinkWithParams = (
  queryParams: Array<{ [key: string]: string }>,
  breadcrumbs: Array<Breadcrumb>,
  mountOrigin: string,
  adId: string | number,
) => {
  const searchResultsBaseUrl = breadcrumbs.find((breadcrumb) => breadcrumb.path.includes("/search/"))?.path;
  if (searchResultsBaseUrl === undefined) throw new Error(`Search results base url not found for ad with id: ${adId}`);
  const makeSearchPageUrl = new URL(searchResultsBaseUrl, mountOrigin);

  queryParams.forEach((param) => {
    const [key, value] = Object.entries(param)[0];
    makeSearchPageUrl.searchParams.append(key, value);
  });
  makeSearchPageUrl.searchParams.sort();
  return makeSearchPageUrl.href;
};

const CreateSearchLinkToDiscoveryPages = (adViewType: string, mountOrigin: string, pathNodes: string[]) => {
  if (!["car-new", "car-used", "car-used-business", "car-leasing-business", "car-leasing"].includes(adViewType)) {
    // Currently only cars have discovery pages
    throw new Error(`Discovery pages not supported for ad type: ${adViewType}`);
  }

  const discoveryPath = getDiscoveryPath(pathNodes);
  return new URL(discoveryPath, mountOrigin).href;
};

const useSearchLinks = (ad: MobilityAd) => {
  const { isPreview, logger, breadcrumbs, mountOrigin } = useAppConfig();
  const { adId } = useAdViewMeta();
  if (isPreview) return null;

  try {
    switch (ad.adViewType) {
      case "car-new":
      case "car-used":
      case "car-used-business":
      case "car-leasing-business":
      case "car-leasing": {
        if (!ad.car_model_taxonomy) return null;

        if (!ad.car_model_taxonomy.parent) {
          if (ad.car_model_taxonomy.id === 2252) {
            return null;
          } // 2252 = "Other makes" and should not return a search link
          const make = ad.car_model_taxonomy.value;
          return [
            {
              url: CreateSearchLinkToDiscoveryPages(ad.adViewType, mountOrigin, [make]),
              text: make,
            },
          ];
        }

        const taxonomy = extractCarTaxonomy(ad.car_model_taxonomy);

        if (!taxonomy) {
          return null;
        }

        const { make, series, model } = taxonomy;

        if (!series) {
          return [
            {
              url: CreateSearchLinkToDiscoveryPages(ad.adViewType, mountOrigin, [make]),
              text: make,
            },
            {
              url: CreateSearchLinkToDiscoveryPages(ad.adViewType, mountOrigin, [make, model]),
              text: `${make} ${model}`,
            },
          ];
        }

        return [
          {
            url: CreateSearchLinkToDiscoveryPages(ad.adViewType, mountOrigin, [make]),
            text: make,
          },
          {
            url: CreateSearchLinkToDiscoveryPages(ad.adViewType, mountOrigin, [make, series]),
            text: `${make} ${series}`,
          },
          {
            url: CreateSearchLinkToDiscoveryPages(ad.adViewType, mountOrigin, [make, series, model]),
            text: `${make} ${series} ${model}`,
          },
        ];
      }
      case "boat-sale":
      case "boat-new-sale":
      case "boat-rent": {
        const searchUrls = [];
        if (ad.make) {
          const make = ad.make.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams([{ make: `${ad.make.id}` }], breadcrumbs, mountOrigin, adId),
            text: make,
          });
        }

        if (ad.boat_class) {
          const boatClass = ad.boat_class.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams([{ class: `${ad.boat_class.id}` }], breadcrumbs, mountOrigin, adId),
            text: boatClass,
          });
        }

        return searchUrls;
      }
      case "boat-wanted": {
        if (ad.boat_class) {
          const boatClass = ad.boat_class.value;
          return [
            {
              url: CreateSearchLinkWithParams([{ class: `${ad.boat_class.id}` }], breadcrumbs, mountOrigin, adId),
              text: boatClass,
            },
          ];
        }
        return null;
      }
      case "boat-motor": {
        if (ad.motor_type) {
          const motorType = ad.motor_type.value;
          return [
            {
              url: CreateSearchLinkWithParams([{ type: `${ad.motor_type.id}` }], breadcrumbs, mountOrigin, adId),
              text: motorType,
            },
          ];
        }
        return null;
      }

      case "mobile-home": {
        const searchUrls = [];
        if (ad.make) {
          const make = ad.make.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams([{ make: `${ad.make.id}` }], breadcrumbs, mountOrigin, adId),
            text: make,
          });
        }

        if (ad.segment) {
          const segment = ad.segment.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams(
              [{ mobile_home_segment: `${ad.segment.id}` }],
              breadcrumbs,
              mountOrigin,
              adId,
            ),
            text: segment,
          });
        }

        return searchUrls;
      }

      case "caravan": {
        const searchUrls = [];
        if (ad.make) {
          const make = ad.make.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams([{ make: `${ad.make.id}` }], breadcrumbs, mountOrigin, adId),
            text: make,
          });
        }

        if (ad.segment) {
          const segment = ad.segment.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams([], breadcrumbs, mountOrigin, adId),
            text: segment,
          });
        }

        return searchUrls;
      }

      case "moped":
      case "motorcycle": {
        const searchUrls = [];
        if (ad.make) {
          const make = ad.make.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams([{ make: `${ad.make.id}` }], breadcrumbs, mountOrigin, adId),
            text: make,
          });
        }

        if (ad.type) {
          const type = ad.type.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams([{ type: `${ad.type.id}` }], breadcrumbs, mountOrigin, adId),
            text: type,
          });
        }

        return searchUrls;
      }
      case "atv":
      case "snowmobile": {
        const searchUrls = [];
        if (ad.make) {
          const make = ad.make.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams([{ make: `${ad.make.id}` }], breadcrumbs, mountOrigin, adId),
            text: make,
          });
        }

        if (ad.type) {
          const type = ad.type.value;
          searchUrls.push({ url: CreateSearchLinkWithParams([], breadcrumbs, mountOrigin, adId), text: type });
        }

        return searchUrls;
      }

      case "agriculture-tractor":
      case "agriculture-thresher": {
        if (ad.make) {
          const make = ad.make.value;
          return [
            {
              url: CreateSearchLinkWithParams([{ make: `0.${ad.make.id}` }], breadcrumbs, mountOrigin, adId),
              text: make,
            },
          ];
        }
        return null;
      }

      case "agriculture-tools": {
        if (ad.category) {
          const category = ad.category.value;
          return [
            {
              url: CreateSearchLinkWithParams([{ type: `${ad.category.id}` }], breadcrumbs, mountOrigin, adId),
              text: category,
            },
          ];
        }
        return null;
      }

      case "bus": {
        const searchUrls = [];
        if (ad.make) {
          const make = ad.make.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams([{ make: `${ad.make.id}` }], breadcrumbs, mountOrigin, adId),
            text: make,
          });
        }

        if (ad.type) {
          const segment = ad.type.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams([{ bus_segment: `${ad.type.id}` }], breadcrumbs, mountOrigin, adId),
            text: segment,
          });
        }

        return searchUrls;
      }

      case "truck": {
        const searchUrls = [];
        if (ad.make) {
          const make = ad.make.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams([{ make: `${ad.make.id}` }], breadcrumbs, mountOrigin, adId),
            text: make,
          });
        }

        if (ad.segment) {
          const segment = ad.segment.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams([{ truck_segment: `${ad.segment.id}` }], breadcrumbs, mountOrigin, adId),
            text: segment,
          });
        }

        return searchUrls;
      }

      case "construction": {
        const searchUrls = [];
        if (ad.make) {
          const make = ad.make.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams([{ make: `${ad.make.id}` }], breadcrumbs, mountOrigin, adId),
            text: make,
          });
        }

        if (ad.segment) {
          const segment = ad.segment.value;
          searchUrls.push({
            url: CreateSearchLinkWithParams(
              [{ construction_segment: `${ad.segment.id}` }],
              breadcrumbs,
              mountOrigin,
              adId,
            ),
            text: segment,
          });
        }

        return searchUrls;
      }

      default:
        return null;
    }
  } catch (e: unknown) {
    // Narrow logger to pino vs Console to call with the right signature
    const isPinoLogger = (l: Console | Logger): l is Logger => "level" in l;
    if (isPinoLogger(logger)) {
      logger.error({ error: e }, "SearchLinks error");
    } else {
      logger.error("SearchLinks error", e);
    }
    return null;
  }
};
